import React from 'react';
import { NoteType, Scan, TaskNote } from '@bringg/types/types/task';
import DateDisplay from '../date-display/date-display';
import { MapData } from '../../components/mapbox/mapbox';
import { MarkerData } from 'types/maps.consts';
import { Divider } from 'antd';
import LocationIcon from '../../components/location-icon/location-icon';
import { validateLocation } from '../../services/validation-rules';
import { Translations as AttachmentNameTranslations } from './attachment-name/attachment-name';
import AttachmentImage from './attachment-image/attachment-image';
import AttachmentScans, { Translations as AttachmentScansTranslations } from './attachment-scans/attachment-scans';
import moment from 'moment';

export interface Translations extends AttachmentNameTranslations, AttachmentScansTranslations {}

export const getIndexToAddScans = (taskNotesAttachments: TaskNote[], scans: Scan[]) => {
	if (taskNotesAttachments.length) {
		const lastScanIndex = scans.length - 1;
		const indexToAdd = taskNotesAttachments.findIndex(
			taskNote => moment(taskNote.created_at) < moment(scans[lastScanIndex].created_at)
		);
		if (indexToAdd === -1) {
			return taskNotesAttachments.length;
		}
		return indexToAdd;
	}
	return 0;
};

interface TaskNotesAttachmentsProps {
	taskNotesAttachments: TaskNote[];
	scans?: Scan[];
	dateFormat: string;
	mapData?: MapData;
	translations: Translations;
	timezone?: string;
	setMapMarker: (marker: MarkerData) => void;
}

const TaskNotesAttachments: React.FC<TaskNotesAttachmentsProps> = ({
	taskNotesAttachments,
	scans,
	dateFormat,
	mapData,
	translations,
	timezone,
	setMapMarker
}) => {
	const GetAttachmentNote: React.FC<{ taskNote: TaskNote }> = ({ taskNote }) => {
		const notes = Object.keys(taskNote.note).map((key, index) => {
			const noteContent = taskNote.note[key]?.toString();

			return (
				<div className="task-note-attachment-form" key={index}>
					<div className="task-note-attachment-form-title">
						<span className="task-key">{key}</span>
						<span className="task-content">{noteContent}</span>
					</div>
					<Divider />
				</div>
			);
		});

		return <div className="task-note-attachment-note task-note-attachment-notes">{notes}</div>;
	};

	const attachments = () => {
		const attachmemtsMap = taskNotesAttachments.map((taskNote: TaskNote) => (
			<div className="task-note-attachment" key={taskNote.id} data-test-id={taskNote.id}>
				<div>
					<div className="task-note-attachment-title">
						{taskNote.author_name}
						{mapData && validateLocation(taskNote.lng, taskNote.lat) && (
							<LocationIcon
								setMapMarker={setMapMarker}
								lat={taskNote.lat}
								lng={taskNote.lng}
							></LocationIcon>
						)}
					</div>

					<div className="task-note-attachment-date">
						<DateDisplay date={taskNote.created_at} format={dateFormat} timezone={timezone} />
					</div>

					{taskNote.url && <AttachmentImage taskNote={taskNote} />}
					{taskNote.note && taskNote.type === NoteType.Form && <GetAttachmentNote taskNote={taskNote} />}
				</div>
			</div>
		));

		scans?.length &&
			attachmemtsMap.splice(
				getIndexToAddScans(taskNotesAttachments, scans),
				0,
				<AttachmentScans
					key="task-scans-attachment"
					scans={scans}
					translations={translations}
					dateFormat={dateFormat}
					mapData={mapData}
					timezone={timezone}
					setMapMarker={setMapMarker}
				/>
			);

		return attachmemtsMap;
	};

	return <div className="task-notes-attachments">{attachments()}</div>;
};

export default TaskNotesAttachments;
